import Head from 'next/head';
import {
  Box,
  Typography,
  Grid,
  IconButton,
  Container,
  Link,
  List,
  ListItem,
} from '@mui/material';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRef, useState} from 'react';
import Slider from 'react-slick';
import CloseIcon from '@mui/icons-material/Close';
import {useRouter} from 'next/router';
import {BaseFooter, Expandable} from '@jobseeker/lotus/components';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import Button from '@jobseeker/lotus/elements/Button';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  AiFillFacebook,
  AiFillLinkedin,
  AiFillYoutube,
  AiOutlineHome,
} from 'react-icons/ai';
import {wrapper} from '@jobseeker/common-core/stores';

const PLATFORM = process.env.NEXT_PUBLIC_APP_NAME;
const isAppIIMJobs = PLATFORM === 'iimjobs';

export default function VCNow() {
  const images: Record<string, {src: string[]; description: string}> = {
    'Learn from top-notch institutes': {
      src: ['https://static.iimjobs.com/partner_assets/images/vcnowhover1.png'],
      description:
        'Unlock great learning opportunities, and get certified for your inherent skills from reputed management institutes such as IIM Ahmedabad, IIM Calcutta, IIM Lucknow, IIM Indore, and XLRI Jamshedpur.',
    },
    'Access learning on the go': {
      src: ['https://static.iimjobs.com/partner_assets/images/vcnowhover2.png'],
      description:
        ' Can’t take a career break? Actually, you don’t need to. Learn remotely from our virtual interactive learning-based programmes, best suited for busy working professionals and seamlessly delivered through Direct to Device (D2D) mode during weekends or after office hours.',
    },
    'Interactive pedagogy': {
      src: ['https://static.iimjobs.com/partner_assets/images/vcnowhover3.png'],
      description:
        'Experience a holistic learning environment with a blend of enriching on-campus immersions, quality peer group interactions and intuitive live virtual classroom sessions delivered by highly-distinguished faculty.',
    },
    'Active student support': {
      src: ['https://static.iimjobs.com/partner_assets/images/vcnowhover4.png'],
      description:
        'At every step of your journey, you will receive active support and help so that you stay on track and focus on what really matters – your learning objectives.',
    },
    'Proven expertise in executive education': {
      src: ['https://static.iimjobs.com/partner_assets/images/vcnowhover5.png'],
      description:
        'Join the league of 22,000+ satisfied professionals who have been a part of 200+ batches of varied career-enhancing programmes, acquired new-age skills, and advanced their career paths.',
    },
  };

  const isLarge = useIsLargeDevice(600);

  const router = useRouter();
  const [selectedTitle, setSelectedTitle] = useState<string>(
    Object.keys(images)[0]
  );

  const [expanded, setExpanded] = useState(false);

  const handleReadMore = () => {
    setExpanded(prev => !prev);
  };

  const [activeSection, setActiveSection] = useState<string>('ABOUT');

  const courses = [
    {
      title:
        'Post Graduate Certificate Programme in Family Business Management & Entrepreneurship (PGCP-FB&E) – IIM Indore',
      description: [
        '12 months (Attend Live online lectures every Wednesday and Saturday)',
        'Transform the way that family-run businesses think and operate',
        'Helps the next generation to ace their roles in the family business',
        'Learn how to transition the family business into a professionally run enterprise',
        '9-day Campus Immersion at IIM Indore',
      ],
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/IIM_Indore_FBM&E.png',
      link: 'https://www.iimjobs.com/course/iim-indore-post-graduate-certificate-programme-in-family-business-management-entrepreneurship-2422.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
    },
    {
      title:
        'Advanced Programme in Corporate Strategy and Business Leadership (APCSBL) - IIM Indore',
      description: [
        '8 Months (Direct-to-device on Sundays)',
        'Gain knowledge and perspectives on corporate strategy and business leadership and prepare for a VUCA to BANI World',
        'Identify and exploit new emerging markets, trends and growth opportunity areas',
        '3-day On-Campus Immersion at IIM Indore',
        'Certification of Completion by IIM Calcutta',
      ],
      link: 'https://www.iimjobs.com/course/iim-indore-advance-program-in-corporate-strategy-and-business-leadership-2421.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/IIM_Indore_APCSBL.png',
    },
    {
      title: 'Executive Programme in Business Analytics (EPBA) - SPJIMR',
      description: [
        '8 Months (Live online sessions on Saturdays)',
        'Build a strong foundation on how to use analytics to make the right decisions for your business',
        'Develop data-driven strategies by aligning descriptive and predictive analytics methods',
        'Learn in-demand analytics tools and their real-world applications',
        'SPJIMR Executive Alumni Status and Certification',
      ],
      link: 'https://www.iimjobs.com/course/sp-jain-institute-of-management-and-research-executive-certificate-programme-in-business-analytics-2453.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/SPJIMR_EPBA.png',
    },
    {
      title: 'Advanced Management Programme for Corporate Leaders – IIM Indore',
      description: [
        'Upgrade your leadership skills for the future',
        'Lead in a disruptive era',
        'Anticipate future trends with data insights',
        'Industry-incline curriculum for managing new-age challenges',
        'A 3-day campus module and IIM Indore Alumni Status',
      ],
      link: 'https://www.iimjobs.com/course/IIM-Indore%20%7C%20Advanced%20Management%20Programme%20For%20Corporate%20Leaders-2158.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/IIM_Indore_Corporate_Leadership.png',
    },
    {
      title:
        'IIM Indore | Advanced Human Resource Management Programme for HR Leaders',
      description: [
        '12 Months',
        'Comprehensive, contemporary and contextual curriculum',
        'Program delivered by IIM Indore expert faculties from the HR & OB Domain Area',
        'In-depth knowledge of concepts and real-life application of HR field imparted along with practical case studies',
        'Prepares Future HR senior leaders',
      ],
      link: 'https://www.iimjobs.com/course/IIM-Indore%20%7C%20Advanced%20Human%20Resource%20Management%20Programme%20for%20HR%20Leaders-2259.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/IIM_Indore_HR_management (1).png',
    },
    {
      title:
        'IIM Ahmedabad | Executive Programme In Advanced Business Analytics',
      description: [
        '4 Months',
        'Increased quality of interactions and peer group learning among participants',
        'Specifically designed for working executives with all the flexibility benefits of interactive onsite learning',
        'Enables superior performance on the job',
      ],
      link: 'https://www.iimjobs.com/course/iim-ahmedabad-executive-programme-in-advanced-business-analytics-2472.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/IIMA_EPABA_IIM_Jobs_500_x_500.jpg',
    },
    {
      title: 'IIM Lucknow | Executive Programme In HR Analytics',
      description: [
        '6 Months',
        'Get Acquainted with the Importance of Analytics in HR',
        'Gain from Expert Faculty of IIM Lucknow',
        'Certificate of Completion from IIM Lucknow',
        'Campus Module at the Scenic IIM Lucknow Campus',
      ],
      link: 'https://www.iimjobs.com/course/iim-lucknow-executive-programme-in-hr-analytics-2471.html?utm_source=IIMJobs&utm_medium=Microsite&utm_campaign=VCNowMicrosite',
      imageSrc:
        'https://static.iimjobs.com/partner_assets/images/EPHRA_IIM_Jobs_500_x_500.jpg',
    },
  ];

  const testimonials = [
    {
      image:
        'https://static.iimjobs.com/partner_assets/images/John_Mathew_Havas_Life_Sorento.png',
      name: 'John Mathew',
      position: 'Vice President',
      company: 'Havas Life Sorento',
      text: "There are a lot of things we do daily as managers, but we lack fundamentals, theory, and skillsets. The Senior Management Programme at IIM Ahmedabad provided me with all the learning that will enable me to move forward. VCNow delivered an immersive learning experience. The classroom doesn't feel like a virtual one at first. You will feel as if you are attending a real classroom. You can interact with your classmates and ask questions to your professors. Besides, you make lifelong friends with people from different industries, and that is priceless.",
      linkedin: 'https://www.linkedin.com/in/jhnmathew/?originalSubdomain=in',
    },
    {
      image:
        'https://static.iimjobs.com/partner_assets/images/Sheirry_Singh_Ernst_Young.jpg',
      name: 'Sheirry Singh',
      position: 'Assistant Manager HR',
      company: 'Ernst & Young',
      text: "VCNow's support was instrumental in delivering an excellent learning experience in my HR Analytics programme by IIM Lucknow. This course has given me hands-on experience with analytical tools that I'll use to assess and analyse HR data. I'll be expected to handle a lot of number crunching and take quick action to employee queries and recruitment concerns. This course prepares me to confront these challenges head-on and make data-backed decisions in real-world scenarios, thanks to IIM Lucknow’s stellar faculty. Besides, I interacted with over 100+ HR professionals from various industries and gained from their experiences.",
      linkedin:
        'https://www.linkedin.com/in/sheirry-singh-3b095112/?originalSubdomain=in',
    },
    {
      image:
        'https://static.iimjobs.com/partner_assets/images/Leela _Rakesh_Reddy_Oracle.jpg',
      name: 'Leela Rakesh Reddy B',
      position: 'Senior Principal Consultant',
      company: 'Oracle',
      text: 'The Advanced Programme in Data Science from IIM Calcutta gave a broader view of the Analytics and Data Science environment and a good jump start to plunge deeper into specific areas. Talks by industry people were very insightful and motivating. The course has created a strong base in all modules and this has equipped me to pursue specific areas of demand.',
      linkedin: 'https://www.linkedin.com/in/leelarr/?originalSubdomain=uk',
    },
    {
      image:
        'https://static.iimjobs.com/partner_assets/images/Abhay Karkun_Adani_Group.jpg',
      name: 'Abhay Karkun',
      position: 'Manager HR',
      company: 'Adani Group',
      text: 'I signed up for XLRI’s Labour Laws Programme to understand the modifications in labour, industrial, and employment laws and the 4 new codes. The course is well-paced and comprehensive, and I gained a great deal of knowledge about each of the laws. If any industrial unrest or problems arise, as an HR manager I must react accordingly, as this course prepares me for it. Professor P.K. Padhi and others will take you on an immersive learning journey with their excellent storytelling skills and ability to present complex information in a simple manner. I am now familiar with most of the law and I anticipate that I will be able to apply it in my organisation.',
      linkedin:
        'https://www.linkedin.com/in/abhay-karkun-845a296b/?originalSubdomain=in',
    },
    {
      image:
        'https://static.iimjobs.com/partner_assets/images/Girish_Samal.png',
      name: 'Girish Samal',
      position: 'Head of Sales & Marketing',
      company: 'Merritt Innovative Solutions India Pvt. Ltd.',
      text: 'The only method to master anything is to learn from the masters. Thus, I chose IIM Ahmedabad for my Executive Programme in Business Analytics because it delivers unmatched learning experiences, pedagogy, case studies, and involvement. IIMA is second to none when it comes to providing this type of course. The programme covered all aspects of analytics in greater detail. It will help us make informed decisions by managing data to derive actionable insights and intelligence, which is crucial to long‐term growth. The expert team at VCNow has worked closely with our cohort to assist us with our learning journey.',
    },
  ];

  const videos = [
    {
      title:
        'How to be at the leading edge of the Aviation & Aeronautics sector?',
      url: 'https://www.youtube.com/watch?v=zEprsSRhaoY',
    },
    {
      title: 'The ins and outs of Financial Reporting & Corporate Governance.',
      url: 'https://www.youtube.com/watch?v=m9iAz7IrCMc&t=2364s',
    },
    {
      title:
        'Unveil the secrets of successful communication strategies with IIMC Professors.',
      url: 'https://www.youtube.com/watch?v=B7PJuHlLm0U&t=6s',
    },
  ];
  const perks = [
    {
      icon: 'https://static.iimjobs.com/partner_assets/images/VC_Now_Video_conferencing.svg',
      text: 'VCNow was founded nearly two decades ago by professionals who had played a pioneering role in enabling video conferencing based executive learning in the country.',
    },
    {
      icon: 'https://static.iimjobs.com/partner_assets/images/VC_Now_Virtual_Class_Rooms.svg',
      text: 'Our large network of 60 HD virtual classroom centers in 27 cities in India creates an immersive learning experience similar to a physical classroom.',
    },
    {
      icon: 'https://static.iimjobs.com/partner_assets/images/VC_Now_Effective_Learning_Experience.svg',
      text: 'Our long-standing expertise in video technology helps us in delivering an effective learning experience through Direct-to-Device (D2D) and Virtual Classroom modes.',
    },
    {
      icon: 'https://static.iimjobs.com/partner_assets/images/VC_Now_Experience_Academic_Advisors.svg',
      text: 'Our experienced academic advisors assist you throughout your learning journey and let you stay focused on your studies and learn without hassles.',
    },
    {
      icon: 'https://static.iimjobs.com/partner_assets/images/VC_Now_Industry_Oriented_Programmes.svg',
      text: 'Our industry-oriented programmes, supplemented with cutting-edge course modules and engaging pedagogy, are tailored to meet the needs of the ever-evolving business world.',
    },
  ];
  const handleMenuClick = (event: React.MouseEvent, section: string) => {
    router.push(`#${section}`);
    setActiveSection(section);
    // Track event (optional GA tracking)
  };
  const [isCarouselOpen, setCarouselOpen] = useState(false);
  const mainSliderRef = useRef<Slider | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  const carousel_Images = [
    'guidance_and_support',
    'industry_focused_approach',
    'revised_image',
    'video_technology_expertise',
    'virtual_ classrooms',
  ];

  const handleThumbnailClick = (index: number) => {
    setCurrentSlide(index);
    mainSliderRef?.current?.slickGoTo(index);
  };

  const handleCarouselOpen = () => {
    setCarouselOpen(true);
  };

  const handleCarouselClose = () => {
    setCarouselOpen(false);
  };

  const handleBeforeChange = (oldIndex: number, newIndex: number) => {
    setCurrentSlide(newIndex);
  };

  return (
    <>
      <Head>
        <title>
          Executive education programmes by top-ranked B-schools- VCNow
        </title>
        <meta
          name="description"
          content="VCNow is India’s trusted enabler of best executive education programmes by IIM Ahmedabad, IIM Calcutta, IIM Lucknow, IIM Indore, XLRI & SPJain."
        />
        <meta
          name="keywords"
          content="International Business School in India, Top Business Schools in India, Best Business Schools in Mumbai, Executive Education in India, Business Schools in Mumbai, Top B schools in India, Top international business schools in India"
        />
        <link
          rel="shortcut icon"
          href="https://www.iimjobs.com/resources/images/favicon.ico"
        />
      </Head>
      <Box
        sx={{
          mx: 'auto',
          width: {xxs: '100%', md: '80%', lg: '70%', xl: '65%'},
          mt: isLarge ? 7 : 0,
        }}
      >
        <Grid container alignItems="center" spacing={3}>
          <Grid
            item
            xs={12}
            md={4}
            lg={3.5}
            xl={3.3}
            sx={{
              border: '1px solid #f3ede5',
              backgroundColor: '#fdf9f4',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: {xxs: 'center', md: 'flex-start'},
            }}
          >
            <Typography
              mt={{md: 0, xxl: 4}}
              fontSize={15}
              fontWeight={700}
              gutterBottom
              textAlign="center"
              fontFamily={'Roboto'}
            >
              SHOWCASING
            </Typography>
            <Box sx={{textAlign: {xxs: 'center', md: 'left'}}} mt={1}>
              <Image
                src={
                  'https://static.iimjobs.com/partner_assets/images/VCnow_executive_logo.png'
                }
                alt="VC Now, Top International Business School in India"
                width={isLarge ? 100 : 70}
                height={isLarge ? 100 : 70}
              />
            </Box>
            {isLarge && (
              <>
                <Box
                  sx={{backgroundColor: '#cf381e', height: '4px', width: '35%'}}
                />
                <Typography
                  fontSize={24}
                  fontFamily={'Roboto'}
                  fontWeight={700}
                  gutterBottom
                  lineHeight={1.533}
                  mb={2}
                  textAlign="left"
                  mt={3}
                >
                  Want to move up in your career?
                </Typography>
                <Typography
                  variant="body1"
                  mb={{md: 0, xxl: 15}}
                  fontFamily={'Roboto'}
                  sx={{lineheight: 1.375}}
                >
                  Our executive education programmes, delivered by India&apos;s
                  top-ranked B-Schools, will help you get there.
                </Typography>
              </>
            )}
          </Grid>
          <Grid item xs={12} md={8} lg={7.5} xl={7.7} textAlign="center">
            <Box
              sx={{
                cursor: 'pointer',
                position: 'relative',
                display: 'inline-block',
              }}
              onClick={handleCarouselOpen}
            >
              <Image
                src="https://static.iimjobs.com/partner_assets/images/vcnowshowcaseimage1.png"
                alt="Image"
                layout="responsive"
                width={isLarge ? 850 : 300}
                height={isLarge ? 700 : 400}
                sizes="(max-width: 600px) 300px, (max-width: 1024px) 800px, 1000px"
              />
              <Button
                variant="contained"
                sx={{
                  color: 'white',
                  backgroundColor: '#333',
                  position: 'absolute',
                  bottom: 36,
                  left: '50%',
                  transform: 'translateX(-50%)',
                }}
              >
                View Photos
              </Button>
            </Box>
          </Grid>
        </Grid>
        {!isLarge && (
          <Box
            display={'flex'}
            justifyContent={'center'}
            alignItems={'center'}
            flexDirection={'column'}
            sx={{
              border: '1px solid #f3ede5',
              backgroundColor: '#fdf9f4',
              py: 4,
              px: 2,
            }}
          >
            <Box
              sx={{
                backgroundColor: 'red',
                height: '4px',
                width: '35%',
              }}
            />
            <Typography
              fontSize={18}
              fontWeight={700}
              gutterBottom
              lineHeight={1.4}
              mb={1.875}
              fontFamily={'Roboto'}
              textAlign="left"
              mt={3}
            >
              Want to move up in your career?
            </Typography>
            <Typography variant="body1">
              Our executive education programmes, delivered by India&apos;s
              top-ranked B-Schools, will help you get there.
            </Typography>
          </Box>
        )}
        {isCarouselOpen && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 9999,
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: isLarge ? '50%' : '70%',
                height: {xxs: '19%', md: '50%', lg: '69%'},
                borderRadius: '8px',
              }}
            >
              <IconButton
                onClick={handleCarouselClose}
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: isLarge ? 34 : 16,
                  color: 'black',
                  backgroundColor: 'white',
                  width: isLarge ? 40 : 30,
                  height: isLarge ? 40 : 30,
                  zIndex: 10,
                  '&:hover': {
                    backgroundColor: 'white',
                    color: 'black',
                    boxShadow: 'none',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>

              {/* Main Carousel */}
              <Slider
                ref={mainSliderRef}
                infinite
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                arrows
                beforeChange={handleBeforeChange}
              >
                {carousel_Images.map((image, index) => (
                  <Box key={index}>
                    <Image
                      src={`https://static.iimjobs.com/partner_assets/images/vc_now_showcase_${image}.png`}
                      alt={`Carousel Image ${index + 1}`}
                      width={1000}
                      height={400}
                      layout="intrinsic"
                      style={{
                        margin: '0 auto',
                      }}
                    />
                  </Box>
                ))}
              </Slider>

              {/* Thumbnail Carousel */}
              {isLarge && (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 2,
                    gap: 1,
                  }}
                >
                  {carousel_Images.map((image, index) => (
                    <Box
                      key={index}
                      sx={{
                        width: '120px',
                        height: {xxs: '30px', md: '63px', lg: '78px'},
                        padding: '0px',
                        cursor: 'pointer',
                        border:
                          currentSlide === index
                            ? '2px solid #1976d2'
                            : '2px solid transparent',
                        transition: 'border 0.3s',
                      }}
                      onClick={() => handleThumbnailClick(index)}
                    >
                      <Image
                        src={`https://static.iimjobs.com/partner_assets/images/vc_now_showcase_${image}.png`}
                        alt={`Thumbnail ${index + 1}`}
                        width={150}
                        height={100}
                        layout="intrinsic"
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Current Slide Indicator */}
            <Box
              sx={{
                backgroundColor: 'white',
                padding: 1.5,
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: '#333',
                fontWeight: '700',
              }}
            >
              <Typography variant="body2" fontFamily={'Roboto'}>
                Photo {currentSlide + 1} of {carousel_Images.length}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box component="main" mt={5}>
        {/* Menu Section */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            background: 'white',
            zIndex: 10,
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            paddingY: 3,
            overflowX: 'auto',
            width: '100%',
          }}
        >
          <Container sx={{paddingX: 0, textAlign: 'center'}}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              sx={{width: {xxs: 'max-content', md: '100%'}, flexWrap: 'nowrap'}}
            >
              {[
                'ABOUT US',
                'INSTITUTES',
                'PROGRAMMES',
                'TESTIMONIALS',
                'VIDEOS',
                'WHY US',
                'USP',
              ].map(section => (
                <Grid item key={section}>
                  <Link
                    href={`#${section}`}
                    variant="button"
                    fontFamily={'Roboto'}
                    sx={{
                      color: 'text.primary',
                      fontSize: 22,
                      fontFamily: 'Roboto',
                      textDecoration: 'none',
                      fontWeight: 600,
                      paddingX: 0.75,
                      paddingY: 0.5,
                      borderRadius: 1,
                      transition: 'all 0.3s ease-in-out',
                      '&.active': {
                        color: '#d13319',
                      },
                    }}
                    className={activeSection === section ? 'active' : ''}
                    onClick={event => handleMenuClick(event, section)}
                  >
                    {section.toUpperCase()}
                  </Link>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>
        {/* About Section */}
        <Container id="ABOUT US" sx={{py: 6}}>
          <Box display={'flex'} flexDirection={{md: 'row', xxs: 'column'}}>
            <Typography
              fontSize={{xxs: 25, md: 30}}
              fontFamily={'Roboto'}
              fontWeight={600}
              sx={{
                textAlign: {xxs: 'center', md: 'left'},
              }}
              gutterBottom
              {...(isLarge && {borderRight: '2px solid #f1f1f1', pr: 6})}
            >
              About <br />
              VCNow
            </Typography>
            <Box
              display={'flex'}
              justifyContent={'center'}
              flexDirection={'column'}
              pl={isLarge ? 8 : 2}
            >
              <Typography
                fontFamily={'Roboto'}
                paragraph
                sx={{lineHeight: 2}}
                fontSize={isLarge ? 20 : 18}
                fontWeight={400}
              >
                VCNow is India&apos;s premier enabler of executive education for
                working professionals to gain access to career-enhancing
                upskilling programmes. Our trusted partnership with top-notch
                B-Schools, such as IIM Ahmedabad, IIM Calcutta, IIM Lucknow, IIM
                Indore, and XLRI Jamshedpur, demonstrates our longstanding
                dedication to providing industry-relevant education.
                {expanded && (
                  <>
                    <br />
                    <br />
                    We seamlessly bring executive-focused live learning within
                    everyone&apos;s reach. Participants can attend live online
                    lectures anywhere on their devices during weekends/evenings,
                    allowing maximum flexibility to accommodate their busy work
                    schedules. For a few programmes that require a classroom
                    environment, our countrywide network of virtual classrooms
                    makes it possible for participants to visit a VCNow Center
                    in their city and attend lectures in the HD Video
                    Conferencing-enabled virtual classroom and enjoy nearly the
                    same experience as attending lectures in a real classroom!
                    <br />
                    <br />
                    More than 22,000 professionals have already benefited from
                    our programmes across domains including General Management,
                    Strategic Management, HR, Supply Chain & Operations, Data
                    Sciences, Finance, Communication Strategies, and Business &
                    Corporate Laws. Participants have upskilled themselves,
                    connected with like-minded professionals, earned prestigious
                    certifications, become proud alumni, and often taken their
                    careers to new heights. Our experienced team is always
                    available to assist participants in their learning journey,
                    help them concentrate exclusively on their career goals, and
                    transform them into world-class leaders.
                  </>
                )}
              </Typography>
              <Button
                disableRipple
                variant="text"
                sx={{
                  color: '#8f6534',
                  fontSize: '15px',
                  fontFamily: 'Roboto',
                  display: 'flex',
                  justifyContent: {xxs: 'center', md: 'flex-start'},
                  height: 'fit-content',
                  '&:hover': {
                    backgroundColor: 'inherit',
                    textDecoration: 'underline',
                    boxShadow: 'none',
                  },
                }}
                size="small"
                onClick={handleReadMore}
              >
                {expanded ? 'Read Less' : 'Read More'}
              </Button>
            </Box>
          </Box>
        </Container>
        ;{/* Institutes Section */}
        <Box id="INSTITUTES" sx={{py: 6, px: 4}} mb={10}>
          <Typography
            fontSize={{xxs: 25, md: 30}}
            fontWeight={600}
            align="center"
            fontFamily={'Roboto'}
            gutterBottom
            mb={5}
          >
            Partner Institutes
          </Typography>
          <Grid container spacing={8} justifyContent="center">
            {[
              {
                name: 'IIM Ahmedabad',
                logo: 'https://static.iimjobs.com/partner_assets/images/VCNOW_IIM_Ahmedabad_new_logo.jpg',
              },
              {
                name: 'IIM Calcutta',
                logo: 'https://static.iimjobs.com/partner_assets/images/VCNOW_IIMC.jpg',
              },
              {
                name: 'IIM Lucknow',
                logo: 'https://static.iimjobs.com/partner_assets/images/VCNOW_iim_lucknow_logo.png',
              },
              {
                name: 'IIM Indore',
                logo: 'https://static.iimjobs.com/partner_assets/images/VCNOW_IIM_Indore.jpg',
              },
              {
                name: 'XLRI Jamshedpur',
                logo: 'https://static.iimjobs.com/partner_assets/images/VCNOW_XLRI.png',
              },
              {
                name: 'SPJIMR',
                logo: 'https://static.iimjobs.com/partner_assets/images/VC_NOW_SPJIMR.png',
              },
            ].map((institute, index) => (
              <Grid item key={index} xs={6} md={1.7}>
                <Box textAlign="center">
                  <Box
                    sx={{
                      border: '1px solid #e0e0e0',
                      display: 'inline-block',
                      padding: 1,
                    }}
                  >
                    <Image
                      src={institute.logo}
                      alt={institute.name}
                      width={100}
                      height={100}
                      style={{objectFit: 'contain'}}
                    />
                  </Box>
                </Box>
                <Typography
                  textAlign="center"
                  fontSize={15}
                  sx={{mt: 2, fontWeight: 'bold'}}
                  fontFamily="Roboto"
                >
                  {institute.name}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Container id="PROGRAMMES">
          <Box sx={{border: '1px solid #e0e0e0'}}>
            <Box textAlign="center" mb={3.75} mt={6.25}>
              <Typography
                px={1}
                fontSize={{xxs: 20, md: 30}}
                fontWeight={700}
                color="#2b2b2b"
                lineHeight={1.4}
                fontFamily={'Roboto'}
              >
                Unlock great learning opportunities with our industry-oriented
                programmes
              </Typography>
              <Typography
                fontFamily={'Roboto'}
                fontSize={19}
                fontWeight={600}
                color="secondary.dark"
                mt={1.75}
              >
                Explore our live programmes
              </Typography>
            </Box>

            <Grid container spacing={0} direction="column">
              {courses.map((course, index) => (
                <Grid item key={index}>
                  <Box
                    display="flex"
                    alignItems="center"
                    overflow="hidden"
                    gap={3}
                    p={isLarge ? 4 : 3}
                    flexDirection={{xs: 'column', sm: 'row'}}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                    }}
                  >
                    <Box flex={{xs: '1', md: '4'}}>
                      <Link href={course.link} target="_blank" underline="none">
                        <Typography
                          fontSize={{xxs: 16, md: 18}}
                          fontWeight={500}
                          color="secondary.dark"
                          fontFamily={'Roboto'}
                        >
                          {course.title}
                        </Typography>
                      </Link>
                      <List
                        dense
                        disablePadding={!isLarge}
                        sx={{
                          my: 1,
                          listStyleType: {xs: 'none', md: 'disc'},
                          pl: {xs: 0, md: 2},
                          '& .MuiListItem-root': {
                            display: 'list-item',
                            paddingLeft: 0,
                          },
                        }}
                      >
                        {course.description.map((desc, idx) => (
                          <ListItem key={idx}>
                            <Typography
                              fontFamily={'Roboto'}
                              fontSize={15}
                              fontWeight={400}
                              color="secondary.dark"
                            >
                              {desc}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                      <Button
                        variant="outlined"
                        sx={{
                          fontFamily: 'Roboto',
                          fontSize: '14px',
                          borderColor: '#c3c3c3',
                          px: 2,
                          py: 0.5,
                          height: 'fit-content',
                          '&:hover': {
                            borderColor: '#c3c3c3',
                            backgroundColor: 'transparent',
                          },
                        }}
                        size="small"
                      >
                        <Link
                          href={course.link}
                          target="_blank"
                          underline="none"
                          color={'#8f6534'}
                        >
                          View Course
                        </Link>
                      </Button>
                    </Box>

                    {isLarge && (
                      <Box flex="1">
                        <Link href={course.link} target="_blank">
                          <Image
                            src={course.imageSrc}
                            alt={course.title}
                            width={200}
                            height={150}
                            style={{
                              objectFit: 'cover',
                              width: '100%',
                              height: 'auto',
                            }}
                          />
                        </Link>
                      </Box>
                    )}
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Container>
        <Box
          id="TESTIMONIALS"
          sx={{padding: isLarge ? 4 : 2}}
          width={{xs: '100%', md: '80%'}}
          mx={'auto'}
        >
          <Box>
            <Typography
              fontSize={{xxs: 25, md: 30}}
              fontWeight={600}
              align="center"
              gutterBottom
              fontFamily={'Roboto'}
            >
              Testimonials
            </Typography>
            <Grid container justifyContent={'center'}>
              {testimonials.map((testimonial, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Box
                    display="flex"
                    flexDirection={'column'}
                    alignItems={{xxs: 'flex-start', md: 'center'}}
                    padding={isLarge ? 3 : 2}
                  >
                    <Box
                      display={'flex'}
                      gap={1}
                      justifyContent={'center'}
                      flexDirection={{xxs: 'row', md: 'column'}}
                      alignItems={'center'}
                    >
                      <Image
                        src={testimonial.image}
                        alt={testimonial.name}
                        width={isLarge ? 200 : 100}
                        height={isLarge ? 200 : 100}
                        style={{borderRadius: '50%'}}
                      />
                      <Box
                        display={'flex'}
                        justifyContent={'center'}
                        flexDirection={'column'}
                        alignItems={{xxs: 'flex-start', md: 'center'}}
                        mb={3}
                      >
                        <Box
                          display={'flex'}
                          gap={1}
                          marginTop={2}
                          flexDirection={{xxs: 'column', md: 'row'}}
                          justifyContent={'center'}
                          alignItems={'flex-start'}
                        >
                          <Typography
                            fontSize={18}
                            fontWeight="bold"
                            fontFamily={'Roboto'}
                          >
                            {testimonial.name}
                          </Typography>
                          {testimonial.linkedin && (
                            <a
                              href={testimonial.linkedin}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <AiFillLinkedin size={22} />
                            </a>
                          )}
                        </Box>
                        <Typography
                          fontSize={15}
                          fontWeight={500}
                          color="secondary.dark"
                          fontFamily={'Roboto'}
                        >
                          {testimonial.position}
                        </Typography>
                        <Typography
                          fontSize={15}
                          fontWeight={500}
                          fontFamily={'Roboto'}
                          color="secondary.dark"
                        >
                          {testimonial.company}
                        </Typography>
                      </Box>
                    </Box>
                    <Expandable
                      actionTextColor="#8f6534"
                      sx={{
                        lineHeight: '16px',
                        fontWeight: 500,
                        fontSize: 13,
                        textAlign: {xxs: 'left', md: 'center'},
                      }}
                      normalTextExpand={true}
                      title="My Expanding Section"
                      description={testimonial?.text}
                      maxCharacter={170}
                      expandLabel="See More"
                      compressLabel="See Less"
                    />
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
        <Box
          id="VIDEOS"
          sx={{
            mx: 'auto',
            width: {xxs: '90%', md: '80%'},
            pt: 6,
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography
            fontSize={30}
            fontWeight={600}
            className="heading-primary"
            gutterBottom
            textAlign="center"
            fontFamily={'Roboto'}
          >
            Get ready to learn something new - here&apos;s what our videos can
            help you discover!
          </Typography>

          <Grid container mt={3} justifyContent="center">
            {videos.map((video, index) => (
              <Grid item xs={12} key={index}>
                <Box
                  sx={{
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      overflow: 'hidden',
                      mx: 2,
                      py: 2,
                    }}
                  >
                    <Box sx={{flex: 1, p: 2}}>
                      <Typography
                        fontSize={18}
                        variant="subtitle1"
                        component="a"
                        href={video.url}
                        target="_blank"
                        sx={{textDecoration: 'none', color: 'inherit'}}
                        fontFamily={'Roboto'}
                      >
                        {video.title}
                      </Typography>
                    </Box>
                    {isLarge && (
                      <Box>
                        <Button
                          component="a"
                          href={video.url}
                          target="_blank"
                          variant="outlined"
                          sx={{
                            borderColor: '#c3c3c3',
                            color: '#8f6534',
                            fontSize: '14px',
                            fontWeight: 600,
                            px: 2,
                            py: 0.5,
                            height: 'fit-content',
                          }}
                          size="small"
                        >
                          Watch Now
                        </Button>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box id="WHY US" mb={5} mt={15}>
          <Container>
            <Typography
              fontSize={{xxs: 25, md: 30}}
              fontWeight={600}
              align="center"
              mb={3}
              gutterBottom
              fontFamily={'Roboto'}
            >
              Why Us?
            </Typography>
            <Grid
              container
              alignItems="flex-start"
              mt={5}
              direction={{xs: 'column-reverse', md: 'row'}}
            >
              <Grid item xs={12} md={8}>
                <Grid
                  container
                  py={8}
                  px={4}
                  direction="column"
                  gap={6}
                  sx={{
                    backgroundColor: '#fdf9f4',
                    border: '1px solid #f3ede5',
                    paddingBottom: 2,
                  }}
                >
                  <List disablePadding>
                    {Object.keys(images).map((title, idx) => (
                      <ListItem
                        key={idx}
                        disableGutters
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          pl: 0,
                          mb: 3,
                          cursor: 'pointer',
                        }}
                        onMouseEnter={() => setSelectedTitle(title)}
                      >
                        <Box
                          sx={{
                            width: '8px',
                            height: '8px',
                            backgroundColor: 'red',
                            borderRadius: '50%',
                            position: 'absolute',
                            left: 0,
                            marginTop: '10px',
                          }}
                        />
                        <Box sx={{position: 'relative', pl: 2}}>
                          <Typography
                            fontFamily={'Roboto'}
                            fontSize={18}
                            sx={{
                              color:
                                selectedTitle === title
                                  ? 'secondary.main'
                                  : '#808080',
                              fontWeight: 500,
                              '&:hover': {
                                textDecoration: 'none',
                              },
                            }}
                          >
                            {title}
                          </Typography>
                          <Typography
                            fontFamily={'Roboto'}
                            fontSize={17}
                            fontWeight={400}
                            color={
                              selectedTitle === title
                                ? 'secondary.main'
                                : '#808080'
                            }
                            mt={1}
                          >
                            {images[title]?.description}
                          </Typography>
                        </Box>
                      </ListItem>
                    ))}
                  </List>
                </Grid>
              </Grid>

              <Grid item xs={12} md={4}>
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={2}
                  alignItems="center"
                  justifyContent="flex-start"
                  height="100%"
                >
                  {images[selectedTitle]?.src.map((img, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        borderRadius: '8px',
                        overflow: 'hidden',
                      }}
                    >
                      <Image
                        src={img}
                        alt={`Image ${idx + 1}`}
                        width={300}
                        height={500}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Container>
        </Box>
        <Box id="USP" my={5}>
          <Container>
            <Typography
              fontSize={{xxs: 25, md: 30}}
              fontWeight={600}
              align="center"
              mb={4}
              fontFamily={'Roboto'}
              gutterBottom
              sx={{
                color: '#333',
              }}
            >
              What Makes Us Unique?
            </Typography>
            <Box
              sx={{
                backgroundColor: '#fdf9f4',
                border: '1px solid #f2e9dd',
                borderRadius: 2,
                py: isLarge ? 6 : 3,
                px: isLarge ? 4 : 1,
              }}
            >
              <Grid container justifyContent="center" alignItems="center">
                {perks.map((perk, idx) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={idx}
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      borderRight: {
                        xs: 'none',
                        sm:
                          idx % 3 === 2 || idx === 4
                            ? 'none'
                            : '1px solid #f2e9dd',
                      },
                      borderBottom: isLarge
                        ? idx < 3
                          ? '1px solid #f2e9dd'
                          : 'none'
                        : idx < 4
                        ? '1px solid #f2e9dd'
                        : 'none',
                    }}
                  >
                    <Box
                      display="flex"
                      flexDirection={{
                        xxs: 'row',
                        md: 'column',
                      }}
                      alignItems={{xxs: 'flex-start', md: 'center'}}
                      textAlign="center"
                      p={3}
                      sx={{
                        boxShadow: 'none',
                        padding: '16px',
                      }}
                    >
                      <Image
                        src={perk.icon}
                        alt={perk.text}
                        width={40}
                        height={40}
                        style={{
                          marginRight: '16px',
                          marginBottom: '16px',
                        }}
                      />
                      <Typography
                        variant="body2"
                        fontFamily={'Roboto'}
                        sx={{
                          textAlign: {
                            xxs: 'left',
                            md: 'center',
                          },
                          fontSize: 15,
                          color: '#666',
                          lineHeight: '1.6',
                        }}
                      >
                        {perk.text}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Container>
        </Box>
      </Box>
      <BaseFooter
        logoUrl="/logo/iimjobs_footer.svg"
        socialLinks={[
          {
            icon: <AiFillFacebook />,
            url: 'https://www.facebook.com/VCNowbyUnifiedCollaborationServices/',
          },
          {
            icon: <AiFillLinkedin />,
            url: 'https://www.linkedin.com/company/vcnow/?viewAsMember=true',
          },
          {
            icon: <AiFillYoutube />,
            url: 'https://www.youtube.com/channel/UCl1KstlwEyVYWhpjau2tmVQ/videos',
          },
          {
            icon: <AiOutlineHome />,
            url: 'http://www.vcnow.in',
          },
        ]}
        headerLabel={`iimjobs.com © ${new Date()
          .getFullYear()
          .toString()}. All rights reserved.`}
        page={''}
        jobLoading={false}
        termsLink={'/terms'}
      />
    </>
  );
}

export const getServerSideProps = wrapper.getServerSideProps(() => async () => {
  if (!isAppIIMJobs) {
    return {
      notFound: true,
    };
  }

  return {
    props: {},
  };
});
